import { z } from 'zod';

import { httpClient } from '@/shared/api/httpClient';

import {
  constellationViewSchema,
  elementTypeSchema,
  elementDetailSchema,
  elementSummarySchema,
  elementOwnersSchema,
  elementWorkCountsSchema,
  elementWorkRollupSchema,
  elementHistoryPageSchema,
  rootElementSchema,
} from '../model/types';
import type {
  OwnerKind,
  ConstellationView,
  CreateElementInput,
  ElementDetail,
  ElementOwners,
  ElementSummary,
  ElementType,
  ElementWorkCounts,
  ElementWorkRollup,
  ElementHistoryPage,
  RootElement,
  CreateRelationInput,
  SetOwnersInput,
  UpdateElementInput,
} from '../model/types';

/**
 * Czyste funkcje endpointów — zero Reacta, zero stanu.
 *
 * Trasy angielskie pod `/api` (decyzja 23: wszystkie ścieżki po angielsku,
 * także w SPA). W dev trafiają tam przez proxy Vite, które jest odwzorowaniem
 * produkcji (jedno pochodzenie, decyzja 69), a nie wygodą.
 */

export async function fetchElement(id: number, signal?: AbortSignal): Promise<ElementDetail> {
  const response = await httpClient.get<unknown>(`/elements/${id}`, {
    ...(signal ? { signal } : {}),
  });

  return elementDetailSchema.parse(response);
}

/**
 * Całe drzewko startowe jednym wywołaniem (decyzja 70: rekurencja wyłącznie
 * po `has`, zależności dołączane bez rozwijania).
 *
 * Ten sam endpoint obsługuje rozwinięcie kliknięciem — wtedy korzeniem jest
 * kliknięty węzeł, a `depth` wynosi 1.
 */
export async function fetchConstellation(
  id: number,
  signal?: AbortSignal,
): Promise<ConstellationView> {
  /*
   * ⛔ Bez `?depth=` (decyzja 174). Serwer ma domyślną głębokość 1, czyli dokładnie tyle, ile
   *   drzewko rysuje bez kliknięcia w trójkąt. Podawanie tu jedynki jawnie nic by nie kupiło,
   *   a byłoby KOPIĄ domyślnej serwera po stronie klienta — rodzina błędu, przez którą przy
   *   progu hasła klient dwa razy przeczytał nieprawdę (decyzja 153).
   * ⚠ Sam parametr w API ZOSTAJE: rozwinięcie kliknięciem i diagnostyka dalej go używają.
   */
  const response = await httpClient.get<unknown>(`/elements/${id}/constellation`, {
    ...(signal ? { signal } : {}),
  });

  return constellationViewSchema.parse(response);
}

/**
 * Liczniki pracy dla całego widocznego rozwinięcia — <b>wsadowo</b>.
 *
 * `POST`, mimo że to odczyt: lista identyfikatorów ma kilkadziesiąt pozycji
 * i nie mieści się sensownie w łańcuchu zapytania. Drzewko woła to
 * **równolegle** z samym drzewkiem, nie po nim.
 */
export async function fetchWorkCounts(
  elementIds: readonly number[],
  signal?: AbortSignal,
): Promise<ElementWorkCounts[]> {
  if (elementIds.length === 0) {
    // Zapytanie o pustą listę nie ma czego zwrócić, a kosztuje przelot.
    return [];
  }

  const response = await httpClient.post<unknown>(
    '/elements/usage',
    { elementIds },
    { ...(signal ? { signal } : {}) },
  );

  return z.array(elementWorkCountsSchema).parse(response);
}

/**
 * Agregat `Σ` — trójka liczb zsumowana po poddrzewie po `has` (decyzja 75).
 *
 * ⚠ Osobne żądanie od `usage`, mimo tych samych liczb: tamto czyta jeden wiersz
 * na element, to **przechodzi graf**. Wspólny endpoint kazałby każdemu ekranowi
 * z trójką lokalną płacić za rekursję, której nie potrzebuje.
 */
export async function fetchWorkRollup(
  elementIds: readonly number[],
  depth?: number,
  signal?: AbortSignal,
): Promise<ElementWorkRollup[]> {
  if (elementIds.length === 0) {
    return [];
  }

  const response = await httpClient.post<unknown>(
    '/elements/rollup',
    // ⛔⛔ Zasięg „cała gałąź" wyraża się POMINIĘCIEM pola, nigdy liczbą (decyzja 171).
    //    Wysłanie tu sufitu serwera znaczyłoby trzymanie u siebie kopii jego reguły —
    //    ta klasa błędu wyszła na produkcji przy progu hasła (decyzja 153) i wychodzi
    //    zawsze cicho, bo kopia jest zgodna dokładnie do dnia zmiany oryginału.
    { elementIds, ...(depth === undefined ? {} : { depth }) },
    { ...(signal ? { signal } : {}) },
  );

  return z.array(elementWorkRollupSchema).parse(response);
}

/**
 * Korzenie grafu — elementy bez rodzica po `has`, jako spis treści konstelacji.
 *
 * `GET`, bo to zwykły odczyt bez listy identyfikatorów w ciele — w odróżnieniu
 * od `usage`/`owners`/`rollup`, gdzie POST wymusza rozmiar zapytania.
 */
export async function fetchRootElements(signal?: AbortSignal): Promise<RootElement[]> {
  const response = await httpClient.get<unknown>(
    '/elements/roots',
    { ...(signal ? { signal } : {}) },
  );

  return z.array(rootElementSchema).parse(response);
}

/**
 * Efektywni właściciele dla całego widocznego rozwinięcia — <b>wsadowo</b>, z proweniencją.
 *
 * ⚠ Nie mylić z `PUT /elements/{id}/owners`: tamto PRZYPISUJE właścicieli jednego
 * elementu, to CZYTA własność wielu. Rozróżnia je obecność identyfikatora
 * w ścieżce, bo czasownik nie wystarczy — odczyt idzie POST-em.
 */
export async function fetchOwners(
  elementIds: readonly number[],
  signal?: AbortSignal,
): Promise<ElementOwners[]> {
  if (elementIds.length === 0) {
    return [];
  }

  const response = await httpClient.post<unknown>(
    '/elements/owners',
    { elementIds },
    { ...(signal ? { signal } : {}) },
  );

  return z.array(elementOwnersSchema).parse(response);
}

export async function createElement(input: CreateElementInput): Promise<ElementSummary> {
  const response = await httpClient.post<unknown>('/elements', input);

  return elementSummarySchema.parse(response);
}

/**
 * Nowa krawędź. Kierunek jest zawsze `from → to`; „odwrotność" (`used-by`, `part-of`)
 * wołający realizuje **zamieniając końce**, a nie osobnym typem — w bazie są dwa typy
 * relacji, nie cztery (decyzja 46).
 */
export async function createRelation(input: CreateRelationInput): Promise<number> {
  const response = await httpClient.post<unknown>('/relations', input);

  return z.number().parse(response);
}

/**
 * Przypisanie właścicieli — **wsadem, wszyscy albo nikt** (decyzja 97).
 *
 * ⚠ `PUT` na **jednym** elemencie; nie mylić z `POST /elements/owners`, które *czyta*
 * własność wielu (rozróżnia je obecność identyfikatora w ścieżce — patrz komentarz przy
 * `fetchOwners`). Serwer sam sprawdza, czy wskazany byt organizacyjny istnieje: między
 * `element_owners` a tabelami Identity **nie ma klucza obcego** (spec 15 pkt 20), więc
 * to jedyna obrona przed wskazaniem kogoś, kogo nie ma.
 *
 * ⭐ Odmowa niesie `kind` i `id` **pierwszego** brakującego właściciela, więc wołający potrafi
 * nazwać winowajcę z listy, którą sam zbudował — bez dopytywania serwera o nazwy (decyzja 96).
 */
export async function setOwners({ elementId, ...body }: SetOwnersInput): Promise<void> {
  await httpClient.put<void>(`/elements/${elementId}/owners`, body);
}

// ── Słownik typów (decyzja 83) ──────────────────────────────────────────────
//
// ⚠ Odczyt jest dla KAŻDEGO zalogowanego (bez listy typów nie da się utworzyć elementu),
// a zmiany są za polityką architekta — która przepuszcza także admina. Uprawnienia sprawdza
// serwer; ukrywanie sekcji w ustawieniach to wygoda, nie kontrola dostępu.

export async function fetchElementTypes(
  activeOnly: boolean,
  signal?: AbortSignal,
): Promise<ElementType[]> {
  const response = await httpClient.get<unknown>(
    `/element-types?activeOnly=${String(activeOnly)}`,
    { ...(signal ? { signal } : {}) },
  );

  return z.array(elementTypeSchema).parse(response);
}

export async function createElementType(name: string): Promise<ElementType> {
  return elementTypeSchema.parse(await httpClient.post<unknown>('/element-types', { name }));
}

/**
 * ⚠ Zmiana nazwy **przepisuje także elementy** — serwer robi to w jednej transakcji.
 * Dlatego po niej unieważniamy nie tylko listę typów, ale i widoki elementów.
 */
export async function renameElementType(id: number, name: string): Promise<ElementType> {
  return elementTypeSchema.parse(await httpClient.patch<unknown>(`/element-types/${id}`, { name }));
}

/** Wycofanie z oferty (albo przywrócenie). Nie rusza elementów, które ten typ mają. */
export async function setElementTypeActive(id: number, isActive: boolean): Promise<ElementType> {
  return elementTypeSchema.parse(
    await httpClient.put<unknown>(`/element-types/${id}/active`, { isActive }),
  );
}

/** ⛔ Serwer odmawia, gdy typ mają żywe elementy — do wycofania z obiegu służy dezaktywacja. */
export async function deleteElementType(id: number): Promise<void> {
  await httpClient.delete<void>(`/element-types/${id}`);
}

export async function updateElement(id: number, input: UpdateElementInput): Promise<void> {
  // PATCH, nie PUT — wysyłamy tylko zmienione pola. Pełne zastąpienie
  // skasowałoby opis przy zmianie samego tytułu.
  await httpClient.patch<void>(`/elements/${id}`, input);
}

export async function deleteElement(id: number): Promise<void> {
  await httpClient.delete<void>(`/elements/${id}`);
}

/**
 * Historia elementu (decyzja 104) — **strona zdarzeń**, nie ogniw.
 *
 * ⚠ Kotwicą jest `before`, nie numer strony: między jednym a drugim żądaniem ktoś może dopisać
 * ogniwo, a wtedy stronicowanie po przesunięciu pokazałoby ten sam wpis dwa razy albo pominęło
 * jeden. Kotwica na `id` tego nie umie.
 */
export async function fetchElementHistory(
  id: number,
  options: { before?: number | null; limit?: number; humanOnly?: boolean; runId?: number },
  signal?: AbortSignal,
): Promise<ElementHistoryPage> {
  const query = new URLSearchParams();

  // ⚠ `undefined` i `null` znaczą tu to samo („bez kotwicy"), ale `0` NIE — dlatego
  // porównanie z `undefined` osobno, a nie skrót `!value`.
  if (options.before !== null && options.before !== undefined) {
    query.set('before', String(options.before));
  }

  if (options.limit !== undefined) {
    query.set('limit', String(options.limit));
  }

  if (options.humanOnly) {
    query.set('humanOnly', 'true');
  }

  if (options.runId !== undefined) {
    query.set('runId', String(options.runId));
  }

  const suffix = query.size > 0 ? `?${query.toString()}` : '';

  const response = await httpClient.get<unknown>(`/elements/${id}/history${suffix}`, {
    ...(signal ? { signal } : {}),
  });

  return elementHistoryPageSchema.parse(response);
}

/** Zdjęcie właściciela **lokalnego** (odziedziczonego się nie zdejmuje — patrz niżej). */
export async function removeOwner(
  elementId: number,
  ownerKind: OwnerKind,
  ownerId: number,
): Promise<void> {
  await httpClient.delete<void>(`/elements/${elementId}/owners/${ownerKind}/${ownerId}`);
}

/**
 * Wyklucza **odziedziczonego** właściciela (spec 9.1: efektywni = dziedziczeni − wykluczeni
 * + lokalni).
 *
 * ⚠⚠ Działa na ten element **i całe poddrzewo pod nim** — to nie jest akcja punktowa
 * i ekran musi to powiedzieć wprost, bo z nazwy nikt się tego nie domyśli.
 */
export async function excludeOwner(
  elementId: number,
  ownerKind: OwnerKind,
  ownerId: number,
): Promise<void> {
  await httpClient.post<void>(`/elements/${elementId}/owners/${ownerKind}/${ownerId}/exclude`, {});
}

/** Cofa wykluczenie — odziedziczony właściciel wraca na element i jego poddrzewo. */
export async function restoreOwner(
  elementId: number,
  ownerKind: OwnerKind,
  ownerId: number,
): Promise<void> {
  await httpClient.delete<void>(`/elements/${elementId}/owners/${ownerKind}/${ownerId}/exclude`);
}
